import React, { Fragment, useMemo } from 'react';

import type { ICashbackLevel } from '../../../../../../types/campaign.types';
import { REBATE_CAMPAIGN_TYPE } from '../../../../../../types/campaign.types';
import { getLevelFillPercent, isGateUnlocked, isLevelUnlocked, isRebateSource } from '../../../../../../utils/variants/rebateHelpers';
import { getSegmentGradientVars } from './RebateProgressBarGradient';
import { useCampaign } from '../../../../../../store/campaignsStore';

import styles from './RebateProgressBar.module.css';
import RebateProgressItem from '../../../RebateProgressItem/RebateProgressItem';
import Image from 'shared/components/Image';

const LOCK_ON_PATH = '/campaigns/icons/lockSymbolOn';
const LOCK_OFF_PATH = '/campaigns/icons/lockSymbolOff';

interface RebateProgressBarProps {
  campaignId: string;
}

interface PillModel {
  levelNumber: number;
  rebatePercent: number;
  unlocked: boolean;
}

const RebateProgressBar: React.FC<RebateProgressBarProps> = ({ campaignId }) => {
  const campaign = useCampaign(campaignId, REBATE_CAMPAIGN_TYPE);
  const rebateCampaign = campaign && isRebateSource(campaign) ? campaign : null;
  const cashbackLevels = rebateCampaign?.cashbackLevels ?? [];
  const userProgress = rebateCampaign?.userProgress ?? null;
  const minWinBetAmount = rebateCampaign?.minWinBetAmount ?? 0;

  const levelCount = cashbackLevels.length;
  const spent = userProgress?.spentBetAmount ?? null;
  const isUnlocked = isGateUnlocked(spent, minWinBetAmount);

  // Segment 0 is the "gate" segment: once the campaign-wide minimum spend AND
  // level 1's own range floor are both cleared, it must render fully filled,
  // even if level 1's own progress formula wouldn't otherwise report 100.
  const firstLevel = cashbackLevels.find(level => level.levelNumber === 1);
  const isFirstSegmentForcedFull =
    spent !== null && firstLevel && spent >= minWinBetAmount && spent >= firstLevel.betRangeFrom;

  // Only a real cashback level (never the synthetic level-0 gate segment) can be "highest".
  const highestLevelNumber = cashbackLevels.length > 0
    ? Math.max(...cashbackLevels.map(level => level.levelNumber))
    : null;

  const pills: PillModel[] = cashbackLevels.map((level) => {
    // No level unlocks before the campaign-wide gate (`minWinBetAmount`), no
    // matter how far `spent` is into that level's own range.
    const unlocked = isLevelUnlocked(spent, minWinBetAmount, level);
    return { levelNumber: level.levelNumber, rebatePercent: level.rebatePercent, unlocked };
  });

  // Figma structure: N pills and N+1 segments —
  // [seg 0][pill 1][seg 1][pill 2][seg 2][pill 3][seg 3 (trailing)].
  // Segment 0 leads up to pill 1; the segment AFTER pill i carries level i's
  // own progress (toward the next pill, or — for the last level — the
  // always-present trailing bar). Segment 0 is a synthetic level 0 spanning
  // [0, level1.betRangeFrom - 1] (or [0, 0] when level 1 starts at 0 or is
  // missing) — RebateProgressItem folds the campaign-wide unlock gate into
  // its own per-level fill formula, so this synthetic level is only there to
  // drive segment 0's layout, not to reuse a real cashback level.
  const segmentLevels = useMemo<ICashbackLevel[]>(() => {
    const zerothLevel: ICashbackLevel = {
      levelNumber: 0,
      betRangeFrom: 0,
      betRangeTo: firstLevel && firstLevel.betRangeFrom > 0 ? firstLevel.betRangeFrom - 1 : 0,
      rebatePercent: 0,
      maxPayoutAmount: 0,
    };
    return [zerothLevel, ...cashbackLevels];
  }, [cashbackLevels, firstLevel]);

  // The unlocked gradient always sweeps dark-to-bright across whatever is
  // CURRENTLY filled/green (not the whole bar), so it reaches full brightness
  // at the current progress edge regardless of how few levels are unlocked.
  // Each segment's own fill fraction (0..1) is derived the same way
  // RebateProgressItem derives its fillPercentage, then normalized against
  // the total filled amount to get that segment's [start, end] position
  // within the filled range.
  const fillFractions = segmentLevels.map((level, index) => {
    const forceFull = index === 0 && isFirstSegmentForcedFull;
    return forceFull ? 1 : getLevelFillPercent(userProgress, level, minWinBetAmount) / 100;
  });
  const totalFilled = fillFractions.reduce((sum, fraction) => sum + fraction, 0);

  let cumulativeFilled = 0;
  const segmentGradientRanges = fillFractions.map((fraction) => {
    const startFraction = totalFilled === 0 ? 0 : cumulativeFilled / totalFilled;
    const endFraction = totalFilled === 0 ? 0 : (cumulativeFilled + fraction) / totalFilled;
    cumulativeFilled += fraction;
    return { startFraction, endFraction };
  });

  return (
    <div className={styles.rebateProgressBar} data-levels={levelCount}>
      {segmentLevels.map((level, index) => (
        <Fragment key={index}>
          <div
            className={styles.segment}
            style={getSegmentGradientVars(
              segmentGradientRanges[index].startFraction,
              segmentGradientRanges[index].endFraction,
              isUnlocked,
            )}
          >
            <RebateProgressItem
              cashbackLevel={level}
              variant='card'
              userProgress={userProgress}
              minWinBetAmount={minWinBetAmount}
              forceFull={index === 0 && isFirstSegmentForcedFull}
              isHighestLevel={level.levelNumber === highestLevelNumber}
            />
          </div>

          {index < levelCount && (
            <div className={styles.pill}>
              <Image
                src={pills[index].unlocked ? LOCK_ON_PATH : LOCK_OFF_PATH}
                manualAffix=".svg"
                className={styles.pillIcon}
              />
              <span className={styles.pillPercent} data-pill-status={pills[index].unlocked ? 'unlocked' : 'locked'}>
                {`${pills[index].rebatePercent.toFixed(1)}%`}
              </span>
            </div>
          )}
        </Fragment>
      ))}
    </div>
  );
};

// fallow-ignore-file
export default RebateProgressBar;
